'use client';

export type StaffRole = 'cashier' | 'kitchen' | 'manager' | 'support';

export interface StaffMember {
  id: string;
  name: string;
  role: StaffRole;
  pin: string;
  avatar: string;
  color: string;
  active: boolean;
  screenPermissions?: Record<string, boolean> | null;
}

export interface AuthSession {
  staffId: string;
  name: string;
  role: StaffRole;
  loginTime: number;
  screenPermissions?: Record<string, boolean> | null;
}

const SESSION_KEY = 'horecapos_session';
const SESSION_COOKIE = 'horecapos_staff_session';

// All screens/functions that can be access-controlled
export const ALL_SCREENS: { key: string; label: string; group: string }[] = [
  // Kassa
  { key: 'pos_terminal', label: 'POS Terminal', group: 'Kassa' },
  { key: 'handheld', label: 'Handheld', group: 'Kassa' },
  { key: 'kitchen', label: 'Keuken Display', group: 'Kassa' },
  { key: 'bar', label: 'Bar Display', group: 'Kassa' },
  // Beheer
  { key: 'dashboard', label: 'Dashboard', group: 'Beheer' },
  { key: 'menu_management', label: 'Menukaart', group: 'Beheer' },
  { key: 'ayce_management', label: 'All You Can Eat', group: 'Beheer' },
  { key: 'product_database', label: 'Product Database', group: 'Beheer' },
  { key: 'floor_plan_editor', label: 'Vloerplan Editor', group: 'Beheer' },
  { key: 'staff_management', label: 'Medewerkers', group: 'Beheer' },
  // Operatie
  { key: 'print_environment', label: 'Print & Display', group: 'Operatie' },
  { key: 'order_history', label: 'Bestelgeschiedenis', group: 'Operatie' },
  // Configuratie
  { key: 'settings', label: 'Instellingen', group: 'Configuratie' },
];

// Default permissions per role (used when no custom permissions set)
export const DEFAULT_ROLE_PERMISSIONS: Record<StaffRole, Record<string, boolean>> = {
  manager: {
    pos_terminal: true, handheld: true, kitchen: true, bar: true,
    dashboard: true, menu_management: true, ayce_management: true,
    product_database: true, floor_plan_editor: true, staff_management: true,
    print_environment: true, order_history: true, settings: true,
  },
  cashier: {
    pos_terminal: true, handheld: true, kitchen: false, bar: false,
    dashboard: false, menu_management: false, ayce_management: false,
    product_database: false, floor_plan_editor: false, staff_management: false,
    print_environment: false, order_history: true, settings: false,
  },
  kitchen: {
    pos_terminal: false, handheld: false, kitchen: true, bar: true,
    dashboard: false, menu_management: false, ayce_management: false,
    product_database: false, floor_plan_editor: false, staff_management: false,
    print_environment: false, order_history: false, settings: false,
  },
  support: {
    pos_terminal: false, handheld: false, kitchen: false, bar: false,
    dashboard: true, menu_management: false, ayce_management: false,
    product_database: false, floor_plan_editor: false, staff_management: false,
    print_environment: false, order_history: true, settings: false,
  },
};

export function getEffectivePermissions(member: StaffMember): Record<string, boolean> {
  if (member.screenPermissions && Object.keys(member.screenPermissions).length > 0) {
    return member.screenPermissions;
  }
  return DEFAULT_ROLE_PERMISSIONS[member.role] ?? DEFAULT_ROLE_PERMISSIONS.cashier;
}

export function canAccess(session: AuthSession | null, screenKey: string): boolean {
  if (!session) return false;
  const perms = session.screenPermissions;
  if (perms && Object.keys(perms).length > 0) {
    return perms[screenKey] === true;
  }
  return DEFAULT_ROLE_PERMISSIONS[session.role]?.[screenKey] ?? false;
}

// getStaff is now a no-op fallback — actual staff comes from Supabase via StaffManagementClient
export function getStaff(): StaffMember[] {
  return [];
}

export function saveStaff(_staff: StaffMember[]): void {
  // No-op: staff is now persisted in Supabase
}

export function getSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) return JSON.parse(stored) as AuthSession;
  } catch {}
  return null;
}

export function setSession(member: StaffMember): void {
  if (typeof window === 'undefined') return;
  const session: AuthSession = {
    staffId: member.id,
    name: member.name,
    role: member.role,
    loginTime: Date.now(),
    screenPermissions: member.screenPermissions ?? null,
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  // Set a cookie so the middleware can verify the session server-side
  document.cookie = `${SESSION_COOKIE}=1; path=/; SameSite=Lax`;
}

export function clearSession(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(SESSION_KEY);
  // Remove the middleware cookie
  document.cookie = `${SESSION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

export function getRoleLabel(role: StaffRole): string {
  const labels: Record<StaffRole, string> = {
    cashier: 'Kassamedewerker',
    kitchen: 'Keuken',
    manager: 'Manager',
    support: 'Support',
  };
  return labels[role];
}

export function getRoleColor(role: StaffRole): string {
  const colors: Record<StaffRole, string> = {
    cashier: 'bg-cyan-500/15 text-cyan-700',
    kitchen: 'bg-amber-500/15 text-amber-700',
    manager: 'bg-violet-500/15 text-violet-700',
    support: 'bg-rose-500/15 text-rose-700',
  };
  return colors[role];
}
